import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Info, AlertTriangle } from "lucide-react";
import MortgageChainFooter from "../../components/MortgageChainFooter/MortgageChainFooter";

export default function MortgageChainTermsOfService() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const emitFormEvent = (type: "call" | "deck") => {
    window.dispatchEvent(
      new CustomEvent("mortgagechain:openForm", { detail: { type } })
    );
  };

  return (
    <div className="bg-[#0A0E27] text-white min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
        * { font-family: 'Inter', sans-serif; }
        h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; }
        .glassmorphic { background: rgba(26,31,58,0.8); backdrop-filter: blur(20px); border: 1px solid rgba(0,212,255,0.2); }
        .gradient-text { background: linear-gradient(90deg,#00D4FF 0%,#8B5CF6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
      `}</style>

      <section className="relative pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 backdrop-blur-sm mb-6">
              <FileText className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-400">
                Terms of Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Last Updated: January 15, 2025
            </p>
          </div>

          <div className="glassmorphic rounded-2xl p-8 mb-8">
            <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-400" />
                Agreement to Terms
              </h3>
              <p className="text-gray-300">
                By accessing or using Crypsey&apos;s platform, you agree to be
                bound by these Terms of Service. Please read them carefully. If
                you do not agree to these terms, do not use our services.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  1. Definitions
                </h2>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      &quot;Crypsey,&quot; &quot;we,&quot; &quot;us,&quot; or
                      &quot;our&quot;
                    </strong>{" "}
                    refers to Crypsey Corp, a Canadian corporation.
                  </li>
                  <li>
                    <strong className="text-white">&quot;Platform&quot;</strong>{" "}
                    refers to Crypsey&apos;s mortgage data analytics platform,
                    including the website, API, dashboards, and all related
                    services.
                  </li>
                  <li>
                    <strong className="text-white">
                      &quot;Customer,&quot; &quot;you,&quot; or &quot;your&quot;
                    </strong>{" "}
                    refers to the individual or entity accessing or using the
                    Platform.
                  </li>
                  <li>
                    <strong className="text-white">&quot;Data&quot;</strong>{" "}
                    refers to anonymized mortgage performance data provided
                    through the Platform.
                  </li>
                  <li>
                    <strong className="text-white">&quot;API&quot;</strong>{" "}
                    refers to our application programming interface for
                    programmatic data access.
                  </li>
                  <li>
                    <strong className="text-white">
                      &quot;Subscription&quot;
                    </strong>{" "}
                    refers to your paid access plan (Starter, Professional,
                    Business, or Enterprise).
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  2. Acceptance of Terms
                </h2>
                <p className="text-gray-300 mb-3">
                  By creating an account, accessing the Platform, or using our
                  services, you acknowledge that:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You have read and understood these Terms of Service</li>
                  <li>You agree to be legally bound by these terms</li>
                  <li>
                    You are at least 18 years of age or the age of majority in
                    your jurisdiction
                  </li>
                  <li>
                    You have the authority to enter into this agreement on
                    behalf of your organization (if applicable)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  3. Description of Services
                </h2>
                <p className="text-gray-300 mb-3">
                  Crypsey provides a SaaS platform that offers:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Mortgage Data Analytics:
                    </strong>{" "}
                    Access to anonymized mortgage performance data from Canadian
                    lenders
                  </li>
                  <li>
                    <strong className="text-white">API Access:</strong>{" "}
                    Programmatic access to data via REST API
                  </li>
                  <li>
                    <strong className="text-white">
                      Dashboards &amp; Reports:
                    </strong>{" "}
                    Interactive visualizations and custom reports
                  </li>
                  <li>
                    <strong className="text-white">Market Insights:</strong>{" "}
                    Aggregate statistics, benchmarks, and trend analysis
                  </li>
                  <li>
                    <strong className="text-white">
                      Blockchain Verification:
                    </strong>{" "}
                    Cryptographic verification of data integrity via Solana
                    blockchain
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  4. Account Registration
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.1 Account Creation
                </h3>
                <p className="text-gray-300 mb-3">
                  To use the Platform, you must:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your account information</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Notify us immediately of any unauthorized access</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.2 Account Responsibilities
                </h3>
                <p className="text-gray-300 mb-3">You are responsible for:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>All activity that occurs under your account</li>
                  <li>
                    Maintaining the confidentiality of API keys and credentials
                  </li>
                  <li>Ensuring your team members comply with these terms</li>
                  <li>Any charges incurred under your account</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.3 Business Accounts
                </h3>
                <p className="text-gray-300 mb-3">
                  If you are registering on behalf of a business or
                  organization:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    You represent that you have authority to bind that entity to
                    these terms
                  </li>
                  <li>The entity will be liable for all use of the account</li>
                  <li>You must provide accurate business information</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  5. Subscription Plans &amp; Pricing
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.1 Available Plans
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 bg-gray-800/50 font-semibold text-white">
                          Plan
                        </th>
                        <th className="text-left py-3 px-4 bg-gray-800/50 font-semibold text-white">
                          Price (CAD/month)
                        </th>
                        <th className="text-left py-3 px-4 bg-gray-800/50 font-semibold text-white">
                          Query Limit
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">Starter</td>
                        <td className="py-3 px-4 text-gray-300">$499</td>
                        <td className="py-3 px-4 text-gray-300">
                          100 queries/month
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">
                          Professional
                        </td>
                        <td className="py-3 px-4 text-gray-300">$1,999</td>
                        <td className="py-3 px-4 text-gray-300">
                          1,000 queries/month
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800">
                        <td className="py-3 px-4 text-gray-300">Business</td>
                        <td className="py-3 px-4 text-gray-300">$4,999</td>
                        <td className="py-3 px-4 text-gray-300">Unlimited</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-300">Enterprise</td>
                        <td className="py-3 px-4 text-gray-300">Custom</td>
                        <td className="py-3 px-4 text-gray-300">Unlimited</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.2 Payment Terms
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Billing Cycle:</strong>{" "}
                    Monthly or annual (prepaid)
                  </li>
                  <li>
                    <strong className="text-white">Payment Methods:</strong>{" "}
                    Credit card, bank transfer, cryptocurrency (USDC/SOL with
                    10% discount)
                  </li>
                  <li>
                    <strong className="text-white">Auto-Renewal:</strong>{" "}
                    Subscriptions automatically renew unless cancelled
                  </li>
                  <li>
                    <strong className="text-white">Failed Payments:</strong>{" "}
                    Service may be suspended after 5 days of non-payment
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.3 Price Changes
                </h3>
                <p className="text-gray-300">
                  We may change pricing with 30 days&apos; notice. Existing
                  customers will be grandfathered at their current rate for the
                  remainder of their billing cycle.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.4 Overage Fees
                </h3>
                <p className="text-gray-300">
                  For plans with query limits, overage charges are $0.10 per
                  query above your limit. You will be notified when approaching
                  your limit.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.5 Refunds
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Free Trial:</strong> No
                    charges if cancelled during trial period
                  </li>
                  <li>
                    <strong className="text-white">
                      Monthly Subscriptions:
                    </strong>{" "}
                    No refunds for partial months
                  </li>
                  <li>
                    <strong className="text-white">
                      Annual Subscriptions:
                    </strong>{" "}
                    Pro-rated refunds available within first 30 days
                  </li>
                  <li>
                    <strong className="text-white">Custom Projects:</strong> 50%
                    refund if cancelled before work begins, no refund after
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  6. Acceptable Use Policy
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.1 Permitted Use
                </h3>
                <p className="text-gray-300 mb-3">
                  You may use the Platform for:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Mortgage market research and analysis</li>
                  <li>Investment due diligence</li>
                  <li>Product development (fintech/proptech)</li>
                  <li>Competitive benchmarking</li>
                  <li>Risk assessment and underwriting</li>
                  <li>Academic and educational research</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.2 Prohibited Use
                </h3>
                <p className="text-gray-300 mb-3">You may NOT:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Attempt to re-identify individuals:
                    </strong>{" "}
                    Use data to identify specific borrowers or properties
                  </li>
                  <li>
                    <strong className="text-white">Resell data:</strong>{" "}
                    Redistribute, resell, or sublicense Platform data without
                    written permission
                  </li>
                  <li>
                    <strong className="text-white">Reverse engineer:</strong>{" "}
                    Attempt to reverse engineer, decompile, or extract source
                    code
                  </li>
                  <li>
                    <strong className="text-white">Overwhelm systems:</strong>{" "}
                    Use automated tools to scrape data or overload our
                    infrastructure
                  </li>
                  <li>
                    <strong className="text-white">Share credentials:</strong>{" "}
                    Share API keys, passwords, or account access with
                    unauthorized parties
                  </li>
                  <li>
                    <strong className="text-white">Violate laws:</strong> Use
                    the Platform for any illegal purpose or in violation of
                    regulations
                  </li>
                  <li>
                    <strong className="text-white">Manipulate data:</strong>{" "}
                    Alter or misrepresent data obtained from the Platform
                  </li>
                  <li>
                    <strong className="text-white">Spam or abuse:</strong> Send
                    unsolicited communications or abuse support channels
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.3 Rate Limits
                </h3>
                <p className="text-gray-300">
                  API requests are subject to rate limits based on your
                  subscription tier. Excessive requests may result in temporary
                  throttling or account suspension.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  7. Data Usage &amp; Intellectual Property
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.1 Data Ownership
                </h3>
                <p className="text-gray-300 mb-3">
                  The anonymized mortgage data provided through the Platform:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Is owned by Crypsey and our data partners</li>
                  <li>Is licensed to you for use during your subscription</li>
                  <li>May not be claimed as your proprietary data</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.2 License Grant
                </h3>
                <p className="text-gray-300 mb-3">
                  We grant you a limited, non-exclusive, non-transferable
                  license to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Access and use Platform data for internal business purposes
                  </li>
                  <li>
                    Create derivative works (reports, models) for internal use
                  </li>
                  <li>
                    Share aggregate statistics with clients (with attribution to
                    Crypsey)
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.3 Attribution
                </h3>
                <p className="text-gray-300 mb-3">
                  When publicly sharing insights derived from Crypsey data, you
                  must:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Include attribution: &quot;Data provided by Crypsey
                    (www.crypsey.com)&quot;
                  </li>
                  <li>Not misrepresent the source or quality of data</li>
                  <li>
                    Not imply Crypsey endorses your analysis or conclusions
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.4 Our Intellectual Property
                </h3>
                <p className="text-gray-300 mb-3">
                  All Platform features, trademarks, logos, and content are
                  owned by Crypsey. You may not:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Use our trademarks without permission</li>
                  <li>
                    Copy or imitate our design, features, or functionality
                  </li>
                  <li>
                    Register domain names confusingly similar to
                    &quot;Crypsey&quot;
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  8. Data Accuracy &amp; Disclaimers
                </h2>

                <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-bold mb-2 flex items-center gap-2 text-amber-200">
                    <AlertTriangle className="w-5 h-5" />
                    Important Data Disclaimers
                  </h3>
                  <p className="text-amber-200">
                    The data provided through the Platform is for informational
                    purposes only. While we strive for accuracy, we cannot
                    guarantee completeness or error-free data.
                  </p>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.1 No Warranty of Accuracy
                </h3>
                <p className="text-gray-300 mb-3">
                  Crypsey provides data &quot;AS IS&quot; without warranties of
                  any kind. We do not guarantee:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Accuracy, completeness, or timeliness of data</li>
                  <li>That data will meet your specific requirements</li>
                  <li>That the Platform will be error-free or uninterrupted</li>
                  <li>That all data is representative of the entire market</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.2 No Investment Advice
                </h3>
                <p className="text-gray-300 mb-3">
                  Crypsey does not provide investment, legal, or financial
                  advice. You are solely responsible for:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Your own investment decisions</li>
                  <li>Conducting independent due diligence</li>
                  <li>
                    Consulting qualified professionals before making financial
                    decisions
                  </li>
                  <li>Complying with all applicable laws and regulations</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.3 Data Verification
                </h3>
                <p className="text-gray-300 mb-3">
                  We implement blockchain verification to ensure data integrity,
                  but this does not guarantee:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Accuracy of data submitted by partners</li>
                  <li>Complete market coverage</li>
                  <li>Real-time updates (data may be delayed)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  9. Privacy &amp; Data Protection
                </h2>
                <p className="text-gray-300 mb-3">
                  Your use of the Platform is also governed by our{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Privacy Policy
                  </Link>
                  , which is incorporated into these Terms by reference. Key
                  points:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>We collect customer account and usage data</li>
                  <li>All mortgage data is fully anonymized (no PII)</li>
                  <li>We comply with PIPEDA and Canadian privacy laws</li>
                  <li>
                    You control your account data and can request deletion
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  10. Service Level &amp; Availability
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  10.1 Uptime Commitment
                </h3>
                <p className="text-gray-300 mb-3">
                  We strive for 99.9% uptime for Business and Enterprise plans.
                  However, we do not guarantee uninterrupted service due to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Scheduled maintenance (with advance notice)</li>
                  <li>Emergency maintenance or security patches</li>
                  <li>
                    Third-party infrastructure issues (AWS, blockchain networks)
                  </li>
                  <li>Force majeure events</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  10.2 Maintenance Windows
                </h3>
                <p className="text-gray-300 mb-3">
                  Scheduled maintenance will be:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Announced at least 48 hours in advance</li>
                  <li>Conducted during low-traffic hours when possible</li>
                  <li>Limited to 4 hours maximum duration</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  10.3 Support
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Starter/Professional:
                    </strong>{" "}
                    Email support (24-48 hour response)
                  </li>
                  <li>
                    <strong className="text-white">Business:</strong> Priority
                    email support (12-24 hour response)
                  </li>
                  <li>
                    <strong className="text-white">Enterprise:</strong>{" "}
                    Dedicated account manager + phone support
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  11. Termination
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  11.1 Termination by You
                </h3>
                <p className="text-gray-300 mb-3">
                  You may cancel your subscription at any time:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Via account settings or by contacting support</li>
                  <li>
                    Cancellation takes effect at end of current billing period
                  </li>
                  <li>You will retain access until subscription expires</li>
                  <li>Refunds subject to Section 5.5</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  11.2 Termination by Us
                </h3>
                <p className="text-gray-300 mb-3">
                  We may suspend or terminate your account immediately if:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You violate these Terms of Service</li>
                  <li>You engage in fraudulent activity</li>
                  <li>Your payment fails repeatedly</li>
                  <li>You attempt to reverse-engineer or abuse the Platform</li>
                  <li>You attempt to re-identify anonymized data</li>
                  <li>Required by law or regulatory order</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  11.3 Effect of Termination
                </h3>
                <p className="text-gray-300 mb-3">Upon termination:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Your access to the Platform will be revoked</li>
                  <li>Your API keys will be deactivated</li>
                  <li>You must cease using all Platform data</li>
                  <li>We may delete your account data after 30 days</li>
                  <li>
                    Provisions regarding confidentiality, indemnification, and
                    liability survive termination
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  12. Limitation of Liability
                </h2>

                <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-bold mb-2 flex items-center gap-2 text-amber-200">
                    <AlertTriangle className="w-5 h-5" />
                    Important Legal Notice
                  </h3>
                  <p className="text-amber-200">
                    Please read this section carefully as it limits our
                    liability to you.
                  </p>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  12.1 No Consequential Damages
                </h3>
                <p className="text-gray-300 mb-3 font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, CRYPSEY SHALL NOT BE
                  LIABLE FOR:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Loss of profits, revenue, or business opportunities</li>
                  <li>Loss of data or inability to access data</li>
                  <li>Investment losses based on Platform data</li>
                  <li>
                    Indirect, incidental, special, or consequential damages
                  </li>
                  <li>Damages resulting from third-party actions</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  12.2 Liability Cap
                </h3>
                <p className="text-gray-300 mb-3">
                  Our total liability to you for any claims arising from your
                  use of the Platform shall not exceed:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      The amount you paid us in the 12 months preceding the
                      claim
                    </strong>
                  </li>
                  <li>
                    <strong className="text-white">
                      OR $10,000 CAD, whichever is less
                    </strong>
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  12.3 Exceptions
                </h3>
                <p className="text-gray-300 mb-3">
                  These limitations do not apply to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Gross negligence or willful misconduct by Crypsey</li>
                  <li>Death or personal injury caused by our negligence</li>
                  <li>Fraud or fraudulent misrepresentation</li>
                  <li>Violations that cannot be limited by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  13. Indemnification
                </h2>
                <p className="text-gray-300 mb-3">
                  You agree to indemnify, defend, and hold harmless Crypsey, its
                  officers, directors, employees, and agents from any claims,
                  damages, losses, or expenses (including legal fees) arising
                  from:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Your violation of these Terms of Service</li>
                  <li>Your misuse of Platform data</li>
                  <li>Your violation of any laws or third-party rights</li>
                  <li>Investment decisions you make based on Platform data</li>
                  <li>Your negligence or misconduct</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  14. Confidentiality
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  14.1 Confidential Information
                </h3>
                <p className="text-gray-300 mb-3">
                  Both parties may receive confidential information. You agree
                  to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Keep API keys, pricing, and non-public features confidential
                  </li>
                  <li>Not disclose our business methods or data sources</li>
                  <li>
                    Use confidential information only for authorized purposes
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  14.2 Exceptions
                </h3>
                <p className="text-gray-300 mb-3">
                  Confidentiality obligations do not apply to information that:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Is publicly available through no fault of yours</li>
                  <li>You already knew before receiving from us</li>
                  <li>Is independently developed by you</li>
                  <li>Must be disclosed by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  15. Changes to Terms
                </h2>
                <p className="text-gray-300 mb-3">
                  We may modify these Terms of Service at any time. When we do:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>We will update the &quot;Last Updated&quot; date</li>
                  <li>Material changes will be emailed to customers</li>
                  <li>Continued use after changes constitutes acceptance</li>
                  <li>If you disagree, you may terminate your subscription</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  16. Governing Law &amp; Dispute Resolution
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  16.1 Governing Law
                </h3>
                <p className="text-gray-300">
                  These Terms are governed by the laws of the Province of
                  Ontario and the federal laws of Canada applicable therein,
                  without regard to conflict of law principles.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  16.2 Jurisdiction
                </h3>
                <p className="text-gray-300">
                  Any disputes shall be resolved in the courts of Ontario,
                  Canada. You consent to the exclusive jurisdiction of these
                  courts.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  16.3 Arbitration (Optional for Enterprise)
                </h3>
                <p className="text-gray-300">
                  Enterprise customers may elect binding arbitration for
                  disputes. Details will be specified in your Enterprise
                  Agreement.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  17. General Provisions
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.1 Entire Agreement
                </h3>
                <p className="text-gray-300">
                  These Terms, together with our Privacy Policy and any
                  Enterprise Agreement, constitute the entire agreement between
                  you and Crypsey.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.2 Severability
                </h3>
                <p className="text-gray-300">
                  If any provision is found unenforceable, the remaining
                  provisions will remain in effect.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.3 No Waiver
                </h3>
                <p className="text-gray-300">
                  Our failure to enforce any right does not waive that right.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.4 Assignment
                </h3>
                <p className="text-gray-300">
                  You may not assign these Terms without our written consent. We
                  may assign these Terms in connection with a merger,
                  acquisition, or sale of assets.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.5 Force Majeure
                </h3>
                <p className="text-gray-300">
                  Neither party is liable for delays caused by events beyond
                  reasonable control (natural disasters, pandemics, war,
                  government actions, etc.).
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  17.6 Export Compliance
                </h3>
                <p className="text-gray-300">
                  You agree to comply with all applicable export laws and
                  regulations when using the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  18. Contact Information
                </h2>
                <p className="text-gray-300 mb-3">
                  For questions about these Terms of Service:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Email:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">General Inquiries:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">Mail:</strong> Crypsey Corp,
                    Toronto, Canada
                  </li>
                  <li>
                    <strong className="text-white">Website:</strong>{" "}
                    <a
                      href="https://www.crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      www.crypsey.com
                    </a>
                  </li>
                </ul>
              </section>

              <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded-lg p-6 mt-8">
                <h3 className="text-xl font-bold mb-4 text-white">
                  Key Points Summary
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>You are responsible for maintaining account security</li>
                  <li>Subscriptions auto-renew unless cancelled</li>
                  <li>Data is provided &quot;AS IS&quot; without guarantees</li>
                  <li>Do not attempt to re-identify anonymized data</li>
                  <li>Do not resell or redistribute Platform data</li>
                  <li>We are not liable for investment losses</li>
                  <li>Disputes are governed by Ontario law</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MortgageChainFooter
        onScheduleCall={() => emitFormEvent("call")}
        onViewDeck={() => emitFormEvent("deck")}
      />
    </div>
  );
}
